import { Alert, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import Autocomplete from '@mui/material/Autocomplete';
import { useMemo, useState, useCallback, useEffect } from 'react';
import SupplyFlowGraph from './SupplyFlowGraph';
import type { DagNodeRow, EdgesWithTrailerPathIndices } from './SupplyFlowGraph';
import type { LpEdgeFlowRow } from '../utils/packingTaskGenerator';
import type { ItemType } from '../schema';
import type { RouteStructure, TrailerPath } from '../utils/routeInference';
import { getSegmentNodeIdForDag } from '../utils/tripDag';
import { validateTripEdgeFlowChange } from '../utils/trailerCargoBan';

export interface SupplyFlowSectionProps {
    dagNodes: DagNodeRow[];
    dagEdges: Array<{ fromNodeIndex: number; toNodeIndex: number }>;
    /** Inferred route (trailer paths) to match edge colors and multi-line edges to route map; optional. */
    routeStructure?: RouteStructure | null;
    /** LP edge flows by item type (from DB) */
    edgeFlowsByItemType: Record<string, LpEdgeFlowRow[]>;
    /** Packing list requirements: eventId -> [{ itemTypeId, quantity }] */
    packingListsByEvent: Map<string, Array<{ itemTypeId: string; quantity: number }>>;
    /** Item types that appear in the trip plan (from packing lists); include category for grouping */
    planItemTypes: ItemType[];
    getItemTypeName: (itemTypeId: string) => string;
    getNodeLabel: (nodeIndex: number) => string;
    /** Controlled from parent (accordion header toggle); view by default */
    editMode?: boolean;
    /** When set, supply flow edits are persisted to DB (trips.lpSolution); called with (itemTypeId, fromNodeIndex, toNodeIndex, value). */
    onEdgeFlowChange?: (itemTypeId: string, fromNodeIndex: number, toNodeIndex: number, value: number) => void;
    /** When false, no conservation (in=out) checks are shown. Use for single-operation supply flow where conservation does not apply. */
    enableConservationChecks?: boolean;
    /** When set (packing operation only), warnings are shown if total outflow from any source exceeds current inventory at that source. */
    getAvailableAtSource?: (itemTypeId: string, fromNodeIndex: number) => number;
    /** Trip planning: trailers using each DAG leg (for trailer–cargo incompatibility warnings). */
    trailersPerEdge?: Map<string, Set<string>> | null;
    /** Normalized incompatibility keys (`trailerId\\tcargoId`). */
    incompatibilityKeys?: Set<string>;
    /**
     * Packing operations: map operation DAG leg indices to trip DAG indices for the same logical edge.
     * When null is returned, trailer–cargo cannot be evaluated for that leg (omit warning).
     */
    resolveTrailerBanIndices?: (fromNodeIndex: number, toNodeIndex: number) => { from: number; to: number } | null;
    /** Trip: error (blocks save in parent). Operation: warning (display only). */
    trailerBanSeverity?: 'error' | 'warning';
}

function edgeKey(from: number, to: number): string {
    return `${from}-${to}`;
}

/**
 * Compute A_i (quantity at node i) from edge flows: A_i = inflow at i.
 * Source has no inflow so A_0 = outflow from source.
 */
function nodeQuantitiesFromFlows(
    dagNodes: DagNodeRow[],
    edgeFlows: Array<{ fromNodeIndex: number; toNodeIndex: number; value: number }>
): number[] {
    const n = dagNodes.length;
    const A = new Array<number>(n).fill(0);
    for (const f of edgeFlows) {
        if (f.toNodeIndex >= 0 && f.toNodeIndex < n && f.value > 0) {
            A[f.toNodeIndex] += f.value;
        }
    }
    let sourceOutflow = 0;
    for (const f of edgeFlows) {
        if (f.fromNodeIndex === 0) sourceOutflow += f.value;
    }
    if (n > 0) A[0] = sourceOutflow;
    return A;
}

/** DAG node index → route node id (same as route map: storage:source:id, eventId, storage:dest:id). */
function getNodeIdByIndex(dagNodes: DagNodeRow[], nodeIndex: number): string {
    const node = dagNodes[nodeIndex];
    if (!node) return '';
    if (node.kind === 'source') return `storage:source:${node.storageLocationId ?? ''}`;
    if (node.kind === 'dest') return `storage:dest:${node.storageLocationId ?? ''}`;
    return node.eventId ?? '';
}

/**
 * Build per-leg list of trailer path indices so supply flow edges match route map colors and multi-line drawing.
 */
function buildEdgesWithTrailerPathIndices(
    dagNodes: DagNodeRow[],
    dagEdges: Array<{ fromNodeIndex: number; toNodeIndex: number }>,
    routeStructure: RouteStructure | null | undefined
): EdgesWithTrailerPathIndices {
    if (!routeStructure || routeStructure.trailerPaths.size === 0) {
        return dagEdges.map(e => ({ ...e, pathIndices: [] }));
    }
    const legKeyToPathIndices = new Map<string, number[]>();
    const paths: TrailerPath[] = [];
    for (const path of Array.from(routeStructure.trailerPaths.values())) paths.push(path);
    paths.forEach((path, pathIndex) => {
        const len = path.segments.length;
        for (let i = 0; i < len - 1; i++) {
            const fromId = getSegmentNodeIdForDag(path.segments[i], i, len);
            const toId = getSegmentNodeIdForDag(path.segments[i + 1], i + 1, len);
            const key = `${fromId}\t${toId}`;
            const arr = legKeyToPathIndices.get(key) ?? [];
            if (!arr.includes(pathIndex)) arr.push(pathIndex);
            legKeyToPathIndices.set(key, arr);
        }
    });
    return dagEdges.map(edge => {
        const fromId = getNodeIdByIndex(dagNodes, edge.fromNodeIndex);
        const toId = getNodeIdByIndex(dagNodes, edge.toNodeIndex);
        const key = `${fromId}\t${toId}`;
        const pathIndices = legKeyToPathIndices.get(key) ?? [];
        return { fromNodeIndex: edge.fromNodeIndex, toNodeIndex: edge.toNodeIndex, pathIndices };
    });
}

/**
 * Get requirement at node index (from packing list; 0 for source/dest).
 */
function requirementAtNode(
    dagNodes: DagNodeRow[],
    nodeIndex: number,
    itemTypeId: string,
    packingListsByEvent: Map<string, Array<{ itemTypeId: string; quantity: number }>>
): number {
    const node = dagNodes[nodeIndex];
    if (!node || node.kind !== 'event' || !node.eventId) return 0;
    const items = packingListsByEvent.get(node.eventId) ?? [];
    const entry = items.find(i => i.itemTypeId === itemTypeId);
    return entry?.quantity ?? 0;
}

export default function SupplyFlowSection({
    dagNodes,
    dagEdges,
    routeStructure,
    edgeFlowsByItemType,
    packingListsByEvent,
    planItemTypes,
    getItemTypeName: _getItemTypeName,
    getNodeLabel,
    editMode = false,
    onEdgeFlowChange,
    enableConservationChecks = true,
    getAvailableAtSource,
    trailersPerEdge,
    incompatibilityKeys,
    resolveTrailerBanIndices: _resolveTrailerBanIndices,
    trailerBanSeverity = 'error',
}: SupplyFlowSectionProps) {
    const [selectedItemType, setSelectedItemType] = useState<ItemType | null>(null);

    /** Plan item types sorted by category then name (for Autocomplete); groupBy hides empty categories when filtered */
    const sortedPlanItemTypes = useMemo(() => {
        const list = [...planItemTypes];
        return list.sort((a, b) => {
            const catA = (a as ItemType & { category?: { name?: string } }).category?.name ?? '';
            const catB = (b as ItemType & { category?: { name?: string } }).category?.name ?? '';
            const cmp = catA.localeCompare(catB);
            if (cmp !== 0) return cmp;
            return (a.name ?? a.id).localeCompare(b.name ?? b.id);
        });
    }, [planItemTypes]);

    /** Total source outflow per item type (for display in selector). */
    const sourceOutflowByItemType = useMemo(() => {
        const out: Record<string, number> = {};
        for (const [itemTypeId, flows] of Object.entries(edgeFlowsByItemType)) {
            const total = (flows ?? []).reduce((sum, f) => sum + (f.fromNodeIndex === 0 ? f.value : 0), 0);
            out[itemTypeId] = total;
        }
        return out;
    }, [edgeFlowsByItemType]);

    /** Default to first item when list is non-empty and none is selected. */
    useEffect(() => {
        if (sortedPlanItemTypes.length > 0) {
            setSelectedItemType(prev => (prev === null ? sortedPlanItemTypes[0] : prev));
        }
    }, [sortedPlanItemTypes]);

    const selectedItemTypeId = selectedItemType?.id ?? null;

    /** Effective edge flows for selected item type (from DB; edits are persisted via onEdgeFlowChange). */
    const effectiveEdgeFlows = useMemo(() => {
        if (!selectedItemTypeId) return [];
        const lpFlows = edgeFlowsByItemType[selectedItemTypeId] ?? [];
        const lpByKey = new Map<string, number>();
        for (const f of lpFlows) {
            lpByKey.set(edgeKey(f.fromNodeIndex, f.toNodeIndex), f.value);
        }
        return dagEdges.map(edge => {
            const key = edgeKey(edge.fromNodeIndex, edge.toNodeIndex);
            const value = lpByKey.get(key) ?? 0;
            return {
                fromNodeIndex: edge.fromNodeIndex,
                toNodeIndex: edge.toNodeIndex,
                value,
            };
        });
    }, [selectedItemTypeId, edgeFlowsByItemType, dagEdges]);

    /** Computed quantity at each node from effective flows */
    const nodeQuantities = useMemo(() => nodeQuantitiesFromFlows(dagNodes, effectiveEdgeFlows), [dagNodes, effectiveEdgeFlows]);

    /** Requirement at each node for selected item type (for "Needs: X" on event nodes) */
    const nodeRequirements = useMemo(() => {
        if (!selectedItemTypeId) return [];
        return dagNodes.map((_, i) => requirementAtNode(dagNodes, i, selectedItemTypeId, packingListsByEvent));
    }, [selectedItemTypeId, dagNodes, packingListsByEvent]);

    /** Per-edge trailer path indices so graph draws multiple lines with route colors */
    const edgesWithTrailerPathIndices = useMemo(
        () => buildEdgesWithTrailerPathIndices(dagNodes, dagEdges, routeStructure),
        [dagNodes, dagEdges, routeStructure]
    );

    /** Warnings: event nodes where planned quantity < requirement */
    const requirementWarnings = useMemo(() => {
        if (!selectedItemTypeId) return [];
        const warnings: Array<{ nodeIndex: number; nodeLabel: string; required: number; planned: number }> = [];
        for (let i = 0; i < dagNodes.length; i++) {
            const node = dagNodes[i];
            if (node.kind !== 'event' || !node.eventId) continue;
            const R = requirementAtNode(dagNodes, i, selectedItemTypeId, packingListsByEvent);
            if (R <= 0) continue;
            const A = nodeQuantities[i] ?? 0;
            if (A < R) {
                warnings.push({
                    nodeIndex: i,
                    nodeLabel: getNodeLabel(i),
                    required: R,
                    planned: A,
                });
            }
        }
        return warnings;
    }, [selectedItemTypeId, dagNodes, packingListsByEvent, nodeQuantities, getNodeLabel]);

    /** Nodes that have both incoming and outgoing edges (true internal/through-nodes). Source has no inflow, sinks have no outflow — skip those. */
    const nodeHasIncomingEdge = useMemo(() => {
        const set = new Set<number>();
        for (const e of dagEdges) set.add(e.toNodeIndex);
        return set;
    }, [dagEdges]);
    const nodeHasOutgoingEdge = useMemo(() => {
        const set = new Set<number>();
        for (const e of dagEdges) set.add(e.fromNodeIndex);
        return set;
    }, [dagEdges]);

    /** Conservation: non-consumable in = out; consumable at event nodes in - out = R. Only when enabled and only for internal nodes with both in and out edges. */
    const conservationViolations = useMemo(() => {
        if (!enableConservationChecks || !selectedItemTypeId) return [];
        const consumable = (selectedItemType as ItemType & { consumable?: boolean }).consumable ?? false;
        const violations: Array<{
            nodeIndex: number;
            nodeLabel: string;
            inflow: number;
            outflow: number;
            /** Set for consumable at event node: expected inflow - outflow */
            required?: number;
        }> = [];
        for (let i = 0; i < dagNodes.length; i++) {
            if (!nodeHasIncomingEdge.has(i) || !nodeHasOutgoingEdge.has(i)) continue;
            const inflow = effectiveEdgeFlows.filter(f => f.toNodeIndex === i).reduce((sum, f) => sum + f.value, 0);
            const outflow = effectiveEdgeFlows.filter(f => f.fromNodeIndex === i).reduce((sum, f) => sum + f.value, 0);
            const node = dagNodes[i];
            const isEventNode = node?.kind === 'event';
            if (consumable && isEventNode) {
                const R = nodeRequirements[i] ?? 0;
                if (inflow - outflow !== R) {
                    violations.push({ nodeIndex: i, nodeLabel: getNodeLabel(i), inflow, outflow, required: R });
                }
            } else {
                if (inflow !== outflow) {
                    violations.push({ nodeIndex: i, nodeLabel: getNodeLabel(i), inflow, outflow });
                }
            }
        }
        return violations;
    }, [
        enableConservationChecks,
        selectedItemTypeId,
        selectedItemType,
        dagNodes,
        nodeHasIncomingEdge,
        nodeHasOutgoingEdge,
        effectiveEdgeFlows,
        nodeRequirements,
        getNodeLabel,
    ]);

    /** Node indices that have a requirement or conservation warning (for graph background highlight). */
    const nodeIndicesWithWarnings = useMemo(() => {
        const set = new Set<number>();
        requirementWarnings.forEach(w => set.add(w.nodeIndex));
        conservationViolations.forEach(v => set.add(v.nodeIndex));
        const out: number[] = [];
        for (const n of set) out.push(n);
        return out;
    }, [requirementWarnings, conservationViolations]);

    /** Edges in topological order (by from then to node index) for table display. */
    const topoOrderedEdges = useMemo(
        () =>
            [...dagEdges].sort((a, b) =>
                a.fromNodeIndex !== b.fromNodeIndex ? a.fromNodeIndex - b.fromNodeIndex : a.toNodeIndex - b.toNodeIndex
            ),
        [dagEdges]
    );

    /** Sources where total outflow exceeds current inventory (packing operation only, when getAvailableAtSource is set). */
    const sourceOverflowWarnings = useMemo(() => {
        if (!getAvailableAtSource || !selectedItemTypeId) return [];
        const fromNodeIndicesSet = new Set(dagEdges.map(e => e.fromNodeIndex));
        const warnings: Array<{ fromNodeIndex: number; nodeLabel: string; outflow: number; available: number }> = [];
        for (const fromNodeIndex of Array.from(fromNodeIndicesSet)) {
            const outflow = effectiveEdgeFlows.filter(f => f.fromNodeIndex === fromNodeIndex).reduce((sum, f) => sum + f.value, 0);
            const available = getAvailableAtSource(selectedItemTypeId, fromNodeIndex);
            if (outflow > available) {
                warnings.push({
                    fromNodeIndex,
                    nodeLabel: getNodeLabel(fromNodeIndex),
                    outflow,
                    available,
                });
            }
        }
        return warnings;
    }, [getAvailableAtSource, selectedItemTypeId, dagEdges, effectiveEdgeFlows, getNodeLabel]);

    const handleEdgeFlowChange = useCallback(
        (fromNodeIndex: number, toNodeIndex: number, value: number) => {
            if (!selectedItemTypeId) return;
            onEdgeFlowChange?.(selectedItemTypeId, fromNodeIndex, toNodeIndex, Math.max(0, value));
        },
        [selectedItemTypeId, onEdgeFlowChange]
    );

    if (dagNodes.length === 0 || dagEdges.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                No route DAG. Add events and set source/destination to see supply flow.
            </Typography>
        );
    }

    if (planItemTypes.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                No item types in trip packing lists. Add items to event packing lists to see supply flow.
            </Typography>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, maxWidth: '100%' }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <Autocomplete
                    size="small"
                    sx={{ minWidth: 320, maxWidth: 520, width: { xs: '100%', sm: 'auto' } }}
                    options={sortedPlanItemTypes}
                    value={selectedItemType}
                    onChange={(_e, newValue) => setSelectedItemType(newValue)}
                    getOptionLabel={option => {
                        const name = option.name ?? option.id;
                        const total = sourceOutflowByItemType[option.id] ?? 0;
                        return `${name} (${total})`;
                    }}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    groupBy={option => (option as ItemType & { category?: { name?: string } }).category?.name ?? 'Uncategorized'}
                    renderOption={(props, option) => {
                        const consumable = (option as ItemType & { consumable?: boolean }).consumable ?? false;
                        const name = option.name ?? option.id;
                        const total = sourceOutflowByItemType[option.id] ?? 0;
                        const label = `${name} (${total})`;
                        return (
                            <li {...props} key={option.id}>
                                {consumable && <LocalGasStationIcon sx={{ fontSize: 18, color: 'warning.main', mr: 1, flexShrink: 0 }} />}
                                <span>{label}</span>
                            </li>
                        );
                    }}
                    renderInput={params => <TextField {...params} label="Item type" placeholder="Select item type" />}
                    renderGroup={params => (
                        <Box key={params.key}>
                            <Typography
                                component="span"
                                variant="caption"
                                sx={{
                                    display: 'block',
                                    px: 1.5,
                                    py: 0.5,
                                    color: 'text.secondary',
                                    fontWeight: 600,
                                }}
                            >
                                {params.group}
                            </Typography>
                            {params.children}
                        </Box>
                    )}
                />
            </Box>

            {selectedItemTypeId && (selectedItemType as ItemType & { consumable?: boolean })?.consumable && (
                <Alert severity="info" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
                    This item type is consumable. The plan assumes the full packing list amount will be consumed at each event.
                </Alert>
            )}

            {!selectedItemTypeId ? (
                <Typography variant="body2" color="text.secondary">
                    Select an item type to see supply flow.
                </Typography>
            ) : (
                <>
                    <SupplyFlowGraph
                        dagNodes={dagNodes}
                        dagEdges={dagEdges}
                        nodeQuantities={nodeQuantities}
                        nodeRequirements={nodeRequirements}
                        edgeFlows={effectiveEdgeFlows}
                        edgesWithTrailerPathIndices={edgesWithTrailerPathIndices}
                        nodeWarningIndices={nodeIndicesWithWarnings}
                        getNodeLabel={getNodeLabel}
                    />

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Quantity per leg
                        </Typography>
                        {selectedItemTypeId && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
                                {(selectedItemType as ItemType & { consumable?: boolean })?.consumable && (
                                    <LocalGasStationIcon sx={{ fontSize: 20, color: 'warning.main' }} aria-hidden />
                                )}
                                <Typography component="span" variant="body2" color="text.secondary">
                                    Total required: {sourceOutflowByItemType[selectedItemTypeId] ?? 0}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                    <TableContainer sx={{ overflowX: 'auto', maxWidth: '100%' }}>
                        <Table size="small" sx={{ minWidth: 280 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>From</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>To</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align="right">
                                        Quantity
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {topoOrderedEdges.map(edge => {
                                    const key = edgeKey(edge.fromNodeIndex, edge.toNodeIndex);
                                    const flowRow = effectiveEdgeFlows.find(
                                        f => f.fromNodeIndex === edge.fromNodeIndex && f.toNodeIndex === edge.toNodeIndex
                                    );
                                    const value = flowRow?.value ?? 0;
                                    const legBan =
                                        trailersPerEdge &&
                                        incompatibilityKeys &&
                                        selectedItemTypeId &&
                                        validateTripEdgeFlowChange(
                                            selectedItemTypeId,
                                            edge.fromNodeIndex,
                                            edge.toNodeIndex,
                                            value,
                                            trailersPerEdge,
                                            incompatibilityKeys
                                        );

                                    return (
                                        <TableRow key={key}>
                                            <TableCell>{getNodeLabel(edge.fromNodeIndex)}</TableCell>
                                            <TableCell>{getNodeLabel(edge.toNodeIndex)}</TableCell>
                                            <TableCell align="right">
                                                {editMode ? (
                                                    <Box
                                                        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}
                                                    >
                                                        <TextField
                                                            type="number"
                                                            size="small"
                                                            inputProps={{ min: 0, step: 1 }}
                                                            value={value}
                                                            onChange={e => {
                                                                const v = parseInt(e.target.value, 10);
                                                                handleEdgeFlowChange(
                                                                    edge.fromNodeIndex,
                                                                    edge.toNodeIndex,
                                                                    isNaN(v) ? 0 : Math.max(0, v)
                                                                );
                                                            }}
                                                            sx={{
                                                                width: 80,
                                                                ...(legBan &&
                                                                    trailerBanSeverity === 'warning' && {
                                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                                            borderColor: 'warning.main',
                                                                        },
                                                                    }),
                                                            }}
                                                            error={trailerBanSeverity === 'error' && Boolean(legBan)}
                                                        />
                                                        {legBan && (
                                                            <Typography
                                                                variant="caption"
                                                                color="warning.main"
                                                                sx={{ maxWidth: 280, textAlign: 'right' }}
                                                            >
                                                                {legBan}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                ) : (
                                                    value
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {requirementWarnings.map(w => (
                        <Alert key={`req-${w.nodeIndex}`} severity="warning" icon={<WarningIcon />}>
                            {w.nodeLabel}: gets {w.planned}, needs {w.required}.
                        </Alert>
                    ))}

                    {conservationViolations.map(v => (
                        <Alert key={`cons-${v.nodeIndex}`} severity="warning" icon={<WarningIcon />}>
                            {v.required !== undefined
                                ? `${v.nodeLabel}: in − out should equal consumed (${v.required}); got ${v.inflow} − ${v.outflow} = ${v.inflow - v.outflow}.`
                                : `${v.nodeLabel}: amounts in (${v.inflow}) and out (${v.outflow}) don't match.`}
                        </Alert>
                    ))}

                    {sourceOverflowWarnings.map(w => (
                        <Alert key={`source-${w.fromNodeIndex}`} severity="warning" icon={<WarningIcon />}>
                            {w.nodeLabel}: planned outflow ({w.outflow}) exceeds current inventory ({w.available}).
                        </Alert>
                    ))}
                </>
            )}
        </Box>
    );
}
